import { useParams } from 'react-router-dom';
import { AppContext } from '../App';
import { State, Lab, useListEc2Query } from '../lib/api';
import { Box, Button, Card, CardContent, Chip, CircularProgress, Container, Divider, Stack, Link, Typography, Paper } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ComputerIcon from '@mui/icons-material/Computer';
import VpnLockIcon from '@mui/icons-material/VpnLock';
import SettingsIcon from '@mui/icons-material/Settings';
import { styled } from '@mui/material/styles';

export interface RouteParams {
  id: string;
}

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  margin: theme.spacing(1, 0),
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  fontWeight: 500,
  marginBottom: theme.spacing(1),
}));

export function Vms() {
  const { id } = useParams();
  
  const { data, isLoading } = useListEc2Query(null, {
    refetchOnWindowFocus: false,
    queryKey: ['listEc2'],
  });

  const downloadFile = (privateKey: string, pemFileName: string) => {
    const element = document.createElement("a");
    const file = new Blob([privateKey], {type: 'text/plain'});
    element.href = URL.createObjectURL(file);
    element.download = pemFileName;
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
  }

  const getStateColor = (state: State) => {
    switch (state) {
      case State.Running:
        return 'success';
      case State.Pending:
        return 'warning';
      case State.Stopping:
      case State.Stopped:
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <AppContext.Consumer>
      {({ configResultData }) => (
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            Virtual Machines
          </Typography>
          
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {data?.listEc2?.filter(ec2 => (!id || (id && ec2.id === id)) && ec2.name.includes('vpn')).length === 0 ? (
                <Box sx={{ width: '100%' }}>
                  <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h6">No virtual machines found</Typography>
                  </Paper>
                </Box>
              ) : (
                data?.listEc2
                  ?.filter(ec2 => (!id || (id && ec2.id === id)) && ec2.name.includes('vpn'))
                  .map(ec2 => {
                    const pemFileName = ec2.name + ".pem";
                    const isRunning = ec2.state === State.Running;
                    
                    return (
                      <Box sx={{ width: { xs: '100%', md: '48%' } }} key={ec2.id}>
                        <StyledCard>
                          <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                              <Typography variant="h5" component="h2">
                                {ec2.name}
                              </Typography>
                              <StyledChip 
                                label={ec2.state} 
                                color={getStateColor(ec2.state)} 
                                size="small"
                              />
                            </Box>
                            
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              ID: <Link href={`#/vms/${ec2.id}`}>{ec2.id}</Link>
                            </Typography>
                            
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              User ID: {ec2.userId}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Lab Type: {ec2.lab}
                            </Typography>
                            
                            {isRunning && (
                              <>
                                <Divider sx={{ my: 2 }} />
                                
                                <Typography variant="body2" gutterBottom>
                                  Public DNS: <Link href={`http://${ec2.publicDnsName}`} target="_blank" rel="noreferrer">{ec2.publicDnsName}</Link>
                                </Typography>
                                
                                {ec2.privateKey !== 'noKey' && (
                                  <Box mt={2}>
                                    <Box display="flex" alignItems="center">
                                      <ComputerIcon color="primary" sx={{ mr: 1 }} />
                                      <Typography variant="subtitle1" fontWeight={500}>SSH Connection</Typography>
                                    </Box>
                                    <Box ml={4} mt={1}>
                                      <StyledButton
                                        variant="outlined"
                                        startIcon={<DownloadIcon />}
                                        onClick={() => downloadFile(ec2.privateKey, pemFileName)}
                                        size="small"
                                      >
                                        Download Key ({pemFileName})
                                      </StyledButton>
                                      <Typography variant="body2" component="div" sx={{ mt: 1, fontFamily: 'monospace', bgcolor: 'background.paper', p: 1, borderRadius: 1 }}>
                                        chmod 0400 {pemFileName}<br />
                                        ssh -i "{pemFileName}" ubuntu@{ec2.publicDnsName}
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                                
                                {ec2.ovpnKey.includes('client') && (
                                  <Box mt={3}>
                                    <Box display="flex" alignItems="center">
                                      <VpnLockIcon color="primary" sx={{ mr: 1 }} />
                                      <Typography variant="subtitle1" fontWeight={500}>VPN Connection</Typography>
                                    </Box>
                                    <Box ml={4} mt={1}>
                                      <StyledButton
                                        variant="outlined"
                                        startIcon={<DownloadIcon />}
                                        onClick={() => downloadFile(ec2.ovpnKey, 'client.ovpn')}
                                        size="small"
                                      >
                                        Download VPN Config (client.ovpn)
                                      </StyledButton>
                                      <Typography variant="body2" component="div" sx={{ mt: 1, fontFamily: 'monospace', bgcolor: 'background.paper', p: 1, borderRadius: 1 }}>
                                        sudo openvpn client.ovpn
                                      </Typography>
                                    </Box>
                                    
                                    <Box mt={2}>
                                      <Typography variant="subtitle2" fontWeight={500}>Kali RDP Connection</Typography>
                                      <Typography variant="body2" component="div" sx={{ mt: 1, fontFamily: 'monospace', bgcolor: 'background.paper', p: 1, borderRadius: 1 }}>
                                        Username: kali<br />
                                        Password: kali<br />
                                        Host: 10.10.0.4
                                      </Typography>
                                    </Box>
                                    
                                    <Box mt={2}>
                                      <Typography variant="subtitle2" fontWeight={500}>Available Machines</Typography>
                                      <Paper variant="outlined" sx={{ mt: 1, p: 1 }}>
                                        {ec2.lab === Lab.Ec2 ? (
                                          <Stack spacing={1}>
                                            <Typography variant="body2">10.10.0.5 - Eternalred</Typography>
                                            <Typography variant="body2">10.10.0.6 - Drupageddon</Typography>
                                            <Typography variant="body2">10.10.0.7 - Windows 10</Typography>
                                            <Typography variant="body2">10.10.0.8 - Netz</Typography>
                                            <Typography variant="body2">10.10.0.9 - Web</Typography>
                                          </Stack>
                                        ) : ec2.lab === Lab.Kaliwin ? (
                                          <Stack spacing={1}>
                                            <Typography variant="body2">10.10.0.4 - Kali</Typography>
                                            <Typography variant="body2">10.10.0.5 - Windows 10</Typography>
                                          </Stack>
                                        ) : ec2.lab === Lab.Multi ? (
                                          <Stack spacing={1}>
                                            <Typography variant="body2">10.10.0.4 - Kali</Typography>
                                            <Typography variant="body2">10.10.0.5 - Multifunktionsserver</Typography>
                                          </Stack>
                                        ) : ec2.lab === Lab.Nmap ? (
                                          <Stack spacing={1}>
                                            <Typography variant="body2">10.10.0.4 - Kali</Typography>
                                            <Typography variant="body2">10.10.0.5 - Multifunktionsserver</Typography>
                                            <Typography variant="body2">10.10.0.6 - Windows-Server</Typography>
                                          </Stack>
                                        ) : null}
                                      </Paper>
                                    </Box>
                                  </Box>
                                )}
                              </>
                            )}
                            
                            <Divider sx={{ my: 2 }} />
                            
                            <Box display="flex" alignItems="center">
                              <SettingsIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                Associated Config: {configResultData?.listEc2Configs.items.filter(c => c.userId === ec2.userId && c.lab === ec2.lab).map((c, i) => (
                                  <Link key={i} href={`#/configs/${c.id}`} sx={{ ml: 0.5 }}>
                                    {c.id}
                                  </Link>
                                ))}
                              </Typography>
                            </Box>
                          </CardContent>
                        </StyledCard>
                      </Box>
                    );
                  })
              )}
            </Box>
          )}
        </Container>
      )}
    </AppContext.Consumer>
  );
}